import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errText, papi } from "../portalApi";
import { waLink } from "./CrmBits";
import { TEMPLATES, shareUrl } from "./crmUtil";

const Shell = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
    <div className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <b>{title}</b>{children}
    </div>
  </div>
);

const inp = "w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40";

export const TemplateModal = ({ c, onClose }) => {
  const live = c.history.find((h) => h.share?.active);
  const link = live ? shareUrl(live.share.token) : "";
  const [k, setK] = useState(c.refill && c.refill.days <= 7 ? "refill" : "reorder");
  const [text, setText] = useState(TEMPLATES.find((t) => t.key === k).text(c, link));
  const pick = (t) => { setK(t.key); setText(t.text(c, link)); };
  const wa = c.whatsapp || c.phone;
  return (
    <Shell title={`Message ${c.name}`} onClose={onClose} testid="crm-template-modal">
      <div className="flex flex-wrap gap-1">{TEMPLATES.map((t) => (
        <button key={t.key} onClick={() => pick(t)} className={`rounded-full px-3 py-1 text-xs font-semibold ${k === t.key ? "bg-teal-600 text-white" : "bg-slate-100"}`} data-testid={`crm-template-item-${t.key}`}>{t.label}</button>))}</div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className={inp} data-testid="crm-template-preview-text" />
      {k === "tracking" && !link && <p className="text-[11px] text-amber-700">Tip: tap "Share tracking link" on a parcel first — the link is then filled in here.</p>}
      <div className="flex gap-2">
        <a href={wa ? waLink(wa, text) : undefined} target="_blank" rel="noreferrer" className={`flex-1 rounded-lg bg-[#25D366] py-2 text-center text-sm font-semibold text-white ${wa ? "" : "pointer-events-none opacity-40"}`} data-testid="crm-template-send-whatsapp">Open WhatsApp</a>
        <a href={c.email ? `mailto:${c.email}?body=${encodeURIComponent(text)}` : undefined} className={`flex-1 rounded-lg border py-2 text-center text-sm font-semibold ${c.email ? "" : "pointer-events-none opacity-40"}`} data-testid="crm-template-send-email">Open email</a>
      </div>
      <p className="text-[11px] text-slate-400">Opens on your own phone — the Hub never sends messages for you.</p>
    </Shell>
  );
};

export const FollowUpModal = ({ c, onClose, onSaved }) => {
  const [v, setV] = useState({ date: "", note: "" });
  const save = async () => {
    try { await papi("post", "/crm/followups", { key: c.key, name: c.name, ...v }); toast.success("Reminder set — it rings in your Hub bell on the day"); onSaved(); onClose(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <Shell title={`Follow-up for ${c.name}`} onClose={onClose} testid="crm-followup-modal">
      <input type="date" value={v.date} min={new Date(Date.now() + 19800000).toISOString().slice(0, 10)} onChange={(e) => setV({ ...v, date: e.target.value })} className={inp} data-testid="crm-followup-date-picker" />
      <input value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} placeholder="What to do, e.g. call about refill" className={inp} data-testid="crm-followup-note-input" />
      <button onClick={save} disabled={!v.date || !v.note.trim()} className="w-full rounded-lg bg-teal-600 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="crm-followup-save-btn">Save reminder</button>
    </Shell>
  );
};

const TAGS = ["VIP", "Regular", "Wholesale", "New", "Inactive"];

export const ClientModal = ({ c, onClose, onSaved }) => {
  const [v, setV] = useState({ name: c?.name || "", phone: c?.phone || "", whatsapp: c?.whatsapp || "", email: c?.email || "",
    tags: (c?.tags || []).filter((t) => !c?.auto_tags?.includes(t)), addresses: [...(c?.addresses || []), "", ""].slice(0, 2) });
  const [busy, setBusy] = useState(false);
  const set = (k, x) => setV((s) => ({ ...s, [k]: x }));
  const save = async () => {
    setBusy(true);
    try { const r = await papi("put", "/crm/clients", v); toast.success(`${v.name} saved`); onSaved(r.key); onClose(); }
    catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <Shell title={c ? `Edit ${c.name}` : "New client"} onClose={onClose} testid="crm-client-modal">
      <input value={v.name} disabled={!!c} onChange={(e) => set("name", e.target.value)} placeholder="Full name (as on parcels)" className={`${inp} disabled:bg-slate-100`} data-testid="crm-modal-name-input" />
      <div className="grid grid-cols-2 gap-2">
        <input value={v.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone, e.g. +1 813 555 0142" className={inp} data-testid="crm-modal-phone-input" />
        <input value={v.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="WhatsApp (if different)" className={inp} data-testid="crm-modal-whatsapp-input" />
      </div>
      <input value={v.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className={inp} data-testid="crm-modal-email-input" />
      {v.addresses.map((a, i) => (
        <textarea key={i} value={a} rows={2} onChange={(e) => set("addresses", v.addresses.map((x, j) => (j === i ? e.target.value : x)))}
          placeholder={i ? "Other address (optional)" : "Street, city, state, zip, country"} className={inp} data-testid={`crm-modal-address-input-${i}`} />))}
      <div className="flex flex-wrap gap-1.5">{TAGS.map((t) => {
        const on = v.tags.includes(t);
        return <button key={t} onClick={() => set("tags", on ? v.tags.filter((x) => x !== t) : [...v.tags, t])} className={`rounded-full border px-3 py-1 text-xs font-semibold ${on ? "border-teal-600 bg-teal-600 text-white" : "bg-white"}`} data-testid={`crm-modal-tag-${t}`}>{t}</button>;
      })}</div>
      <p className="text-[11px] text-slate-400">New and Inactive are also set automatically from orders. Address changes are used by new order sheets too.</p>
      <div className="flex justify-end gap-2"><button onClick={onClose} className="rounded-lg border px-3 py-1.5 text-sm" data-testid="crm-modal-cancel-btn">Cancel</button>
        <button onClick={save} disabled={busy || !v.name.trim()} className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="crm-modal-save-btn">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Save</button></div>
    </Shell>
  );
};
